import { useEffect, useState } from "react";
import Papa from "papaparse";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Store as StoreIcon, Plus, Pencil, Trash2, Upload, Download, MapPin, AlertTriangle,
  CheckCircle2, XCircle, ShieldAlert,
} from "lucide-react";

const emptyStore = () => ({
  id: null, store_code: "", store_name: "", city: "",
  zone: "", address: "", gps_lat: "", gps_lng: "",
  store_manager_name: "", market_manager_name: "",
  status: "active", go_live_date: "",
});

export default function AdminStores() {
  const [stores, setStores] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyStore());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importReplace, setImportReplace] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeStores, setWipeStores] = useState(false);
  const [wipeUsers, setWipeUsers] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wiping, setWiping] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: z }] = await Promise.all([
        api.get("/stores"), api.get("/zones"),
      ]);
      setStores(s); setZones(z);
    } catch { toast.error("Failed to load stores"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyStore()); setOpen(true); };
  const openEdit = (s) => {
    setForm({
      id: s.id, store_code: s.store_code, store_name: s.store_name, city: s.city,
      zone: s.zone || "",
      address: s.address || "", gps_lat: String(s.gps_lat), gps_lng: String(s.gps_lng),
      store_manager_name: s.store_manager_name || "", market_manager_name: s.market_manager_name || "",
      status: s.status || "active", go_live_date: s.go_live_date || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.store_code.trim() || !form.store_name.trim() || !form.city.trim()) return toast.error("Code, name and city are required");
    const lat = parseFloat(form.gps_lat), lng = parseFloat(form.gps_lng);
    if (!isFinite(lat) || !isFinite(lng)) return toast.error("GPS lat/lng required");
    setSaving(true);
    try {
      const payload = {
        store_code: form.store_code.trim(), store_name: form.store_name.trim(),
        city: form.city.trim(), zone: form.zone,
        address: form.address,
        gps_lat: lat, gps_lng: lng,
        store_manager_name: form.store_manager_name, market_manager_name: form.market_manager_name,
        status: form.status, go_live_date: form.go_live_date,
      };
      if (form.id) { await api.patch(`/stores/${form.id}`, payload); toast.success("Store updated"); }
      else { await api.post("/stores", payload); toast.success("Store added"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/stores/${confirmDelete.id}`);
      toast.success("Store deleted"); setConfirmDelete(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadCurrent = async () => {
    try {
      const { data } = await api.get("/samples/stores.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "stores-current.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download"); }
  };

  const downloadTemplate = () => {
    const csv = [
      "store_code,store_name,city,zone,address,gps_lat,gps_lng,store_manager_name,market_manager_name,status,go_live_date",
      "REL-1001,Reliance Smart Lucknow Hazratganj,Lucknow,Central UP,\"Hazratganj, Lucknow, UP\",26.8467,80.9462,Rajesh Kumar,Amit Gupta,active,2025-08-01",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "stores-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvFile = (file) => {
    if (!file) return;
    setImportResult(null);
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const rows = (res.data || []).map((r) => ({
          store_code: (r.store_code || "").trim(),
          store_name: (r.store_name || "").trim(),
          city: (r.city || "").trim(),
          zone: (r.zone || "").trim(),
          address: (r.address || "").trim(),
          gps_lat: Number(r.gps_lat) || 0,
          gps_lng: Number(r.gps_lng) || 0,
          store_manager_name: (r.store_manager_name || "").trim(),
          market_manager_name: (r.market_manager_name || "").trim(),
          status: (r.status || "active").trim(),
          go_live_date: (r.go_live_date || "").trim(),
        })).filter(r => r.store_code || r.store_name);
        setImportRows(rows);
        if (!rows.length) toast.error("No rows found in CSV");
      },
      error: (err) => toast.error("Parse error: " + err.message),
    });
  };

  const submitImport = async () => {
    if (!importRows.length) return;
    setImporting(true);
    try {
      const { data } = await api.post("/stores/bulk-import", { rows: importRows, replace: importReplace });
      setImportResult(data);
      toast.success(`Imported ${data.created + data.updated} rows`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setImporting(false); }
  };

  const closeImport = () => { setImportOpen(false); setImportRows([]); setImportResult(null); setImportReplace(false); };

  const runWipe = async () => {
    if (wipeConfirm !== "WIPE") return toast.error('Type "WIPE" to confirm');
    setWiping(true);
    try {
      const { data } = await api.post("/admin/wipe-demo", {
        confirm: "WIPE", wipe_stores: wipeStores, wipe_users: wipeUsers,
      });
      toast.success(`Wiped: ${Object.entries(data.deleted).map(([k,v]) => `${v} ${k}`).join(", ")}`);
      setWipeOpen(false); setWipeConfirm(""); setWipeStores(false); setWipeUsers(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setWiping(false); }
  };

  const filtered = stores.filter(s => {
    const q = filter.toLowerCase();
    const matchQ = !q || s.store_name.toLowerCase().includes(q) || s.store_code.toLowerCase().includes(q) || s.city.toLowerCase().includes(q);
    const matchZ = !zoneFilter || (s.zone || "") === zoneFilter;
    return matchQ && matchZ;
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Manage Stores" subtitle="30 Reliance retail stores — real data" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Input data-testid="store-filter-input" placeholder="Filter store, code, city…"
                value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" />
              <select data-testid="store-zone-filter" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}
                className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
                <option value="">All zones</option>
                {zones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="import-stores-btn" onClick={() => setImportOpen(true)} variant="outline">
                <Upload className="h-4 w-4 mr-2" /> Import CSV
              </Button>
              <Button data-testid="add-store-btn" onClick={openNew} className="bg-brand-red hover:bg-[#B31721]">
                <Plus className="h-4 w-4 mr-2" /> Add Store
              </Button>
            </div>
          </div>

          <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="stores-admin-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3">#</th>
                    <th className="text-left px-4 py-3">Code</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">City</th>
                    <th className="text-left px-4 py-3">Zone</th>
                    <th className="text-left px-4 py-3">GPS</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={8} className="text-center py-8 text-gray-500">Loading…</td></tr>}
                  {!loading && filtered.map(s => (
                    <tr key={s.id} className="border-t border-gray-100" data-testid={`store-admin-row-${s.store_code}`}>
                      <td className="px-4 py-3 text-gray-500">{s.store_no}</td>
                      <td className="px-4 py-3 font-mono text-xs">{s.store_code}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{s.store_name}</div>
                        <div className="text-xs text-gray-500 truncate max-w-xs">{s.address}</div>
                      </td>
                      <td className="px-4 py-3">{s.city}</td>
                      <td className="px-4 py-3">
                        {s.zone ? (
                          <Badge className="bg-red-50 text-brand-red hover:bg-red-50 border border-red-200 font-semibold">{s.zone}</Badge>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-brand-red" />
                        {s.gps_lat.toFixed(4)}, {s.gps_lng.toFixed(4)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={s.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-gray-100 text-gray-700 hover:bg-gray-100"}>
                          {s.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button data-testid={`edit-store-${s.store_code}`} size="sm" variant="outline" onClick={() => openEdit(s)} className="mr-1">
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                        <Button data-testid={`delete-store-${s.store_code}`} size="sm" variant="outline"
                          onClick={() => setConfirmDelete(s)}
                          className="text-red-600 border-red-200 hover:bg-red-50">
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-500">No stores match</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Danger zone */}
          <Card className="rounded-xl border-red-200 bg-red-50/40 p-5">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-6 w-6 text-red-600 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-red-800">Danger Zone</div>
                <div className="font-display text-lg font-bold text-gray-900">Wipe demo data before publishing</div>
                <div className="text-sm text-gray-700 mt-1">
                  Removes seeded transactional data (sales, attendance, purchases, expenses, machine logs, issues, cash deposits, reimbursements, OTPs).
                  Optionally also removes the 30 demo stores and all non-test users. The 4 test accounts are always preserved.
                </div>
                <Button data-testid="open-wipe-btn" onClick={() => setWipeOpen(true)} variant="outline" className="mt-3 border-red-300 text-red-700 hover:bg-red-100">
                  <Trash2 className="h-4 w-4 mr-2" /> Wipe demo data…
                </Button>
              </div>
            </div>
          </Card>
        </main>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="store-form-dialog" className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Edit store" : "Add store"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Store code</label>
              <Input data-testid="store-code-input" value={form.store_code} onChange={(e) => setForm({ ...form, store_code: e.target.value })} placeholder="REL-1001" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">City</label>
              <Input data-testid="store-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value.toUpperCase() })} placeholder="Lucknow" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Zone</label>
              <select data-testid="store-zone-select" value={form.zone}
                onChange={(e) => setForm({ ...form, zone: e.target.value })}
                className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                <option value="">— No zone —</option>
                {zones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Store name</label>
              <Input data-testid="store-name-input" value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} placeholder="Reliance Smart Hazratganj" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Address</label>
              <Input data-testid="store-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">GPS Latitude</label>
              <Input data-testid="store-lat-input" inputMode="decimal" value={form.gps_lat}
                onChange={(e) => setForm({ ...form, gps_lat: e.target.value.replace(/[^\d.-]/g, "") })} placeholder="26.8467" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">GPS Longitude</label>
              <Input data-testid="store-lng-input" inputMode="decimal" value={form.gps_lng}
                onChange={(e) => setForm({ ...form, gps_lng: e.target.value.replace(/[^\d.-]/g, "") })} placeholder="80.9462" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Store manager</label>
              <Input data-testid="store-manager-input" value={form.store_manager_name} onChange={(e) => setForm({ ...form, store_manager_name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Market manager</label>
              <Input data-testid="market-manager-input" value={form.market_manager_name} onChange={(e) => setForm({ ...form, market_manager_name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Go-live date</label>
              <Input data-testid="go-live-input" type="date" value={form.go_live_date} onChange={(e) => setForm({ ...form, go_live_date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Status</label>
              <select data-testid="store-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm">
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>
          </div>
          <div className="text-[11px] text-gray-500">
            Tip: get GPS from Google Maps → right-click on the store pin → copy coordinates.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-store-btn" onClick={save} disabled={saving} className="bg-brand-red hover:bg-[#B31721]">
              {form.id ? "Save" : "Add store"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent data-testid="store-delete-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete store?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700">
            This will remove <span className="font-semibold">{confirmDelete?.store_name}</span> ({confirmDelete?.store_code}) permanently.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button data-testid="confirm-delete-store-btn" onClick={del} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => v ? setImportOpen(true) : closeImport()}>
        <DialogContent data-testid="stores-import-dialog" className="max-w-3xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Bulk import stores</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm">
              <div className="font-semibold mb-1">CSV columns (header row required):</div>
              <code className="text-xs text-gray-700 block break-all">
                store_code, store_name, city, zone, address, gps_lat, gps_lng, store_manager_name, market_manager_name, status, go_live_date
              </code>
              <ul className="text-xs text-gray-600 mt-2 space-y-1 list-disc list-inside">
                <li><b>Existing store_codes</b> are updated in place; new codes are inserted</li>
                <li><b>Replace mode</b> deletes ALL current stores first (blocked if any user has store assignments — clear them from Manage Users first)</li>
              </ul>
              <div className="flex gap-2 mt-3">
                <Button data-testid="download-current-stores-btn" size="sm" variant="outline" onClick={downloadCurrent}>
                  <Download className="h-3.5 w-3.5 mr-1" /> Current 30 as CSV
                </Button>
                <Button data-testid="download-stores-template-btn" size="sm" variant="outline" onClick={downloadTemplate}>
                  <Download className="h-3.5 w-3.5 mr-1" /> Empty template
                </Button>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Choose CSV file</label>
              <Input data-testid="stores-csv-file-input" type="file" accept=".csv,text/csv"
                onChange={(e) => handleCsvFile(e.target.files?.[0])}
                className="mt-1 file:mr-3 file:rounded-md file:border-0 file:bg-red-50 file:text-brand-red file:px-3 file:py-2 file:font-semibold" />
            </div>

            {importRows.length > 0 && !importResult && (
              <>
                <div className="rounded-lg border border-gray-200 overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-xs" data-testid="stores-import-preview">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr><th className="text-left p-2">#</th><th className="text-left p-2">Code</th><th className="text-left p-2">Name</th><th className="text-left p-2">City</th><th className="text-left p-2">Zone</th><th className="text-left p-2">GPS</th></tr>
                    </thead>
                    <tbody>
                      {importRows.map((r, i) => (
                        <tr key={`${r.store_code || "row"}-${i}`} className="border-t border-gray-100">
                          <td className="p-2 text-gray-500">{i+1}</td>
                          <td className="p-2 font-mono">{r.store_code}</td>
                          <td className="p-2">{r.store_name}</td>
                          <td className="p-2">{r.city}</td>
                          <td className="p-2">{r.zone || <span className="text-gray-400">—</span>}</td>
                          <td className="p-2 text-gray-500 font-mono text-[10px]">{r.gps_lat}, {r.gps_lng}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input data-testid="replace-mode-checkbox" type="checkbox" checked={importReplace} onChange={(e) => setImportReplace(e.target.checked)} className="accent-brand-red" />
                  Replace mode — delete all current stores before importing
                </label>
              </>
            )}

            {importResult && (
              <div className="grid grid-cols-3 gap-3" data-testid="stores-import-result">
                <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                  <div className="text-[10px] uppercase font-bold text-green-800">Created</div>
                  <div className="font-display text-2xl font-black">{importResult.created}</div>
                </div>
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                  <div className="text-[10px] uppercase font-bold text-blue-800">Updated</div>
                  <div className="font-display text-2xl font-black">{importResult.updated}</div>
                </div>
                <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                  <div className="text-[10px] uppercase font-bold text-red-800">Errors</div>
                  <div className="font-display text-2xl font-black">{importResult.errors.length}</div>
                </div>
                {importResult.errors.length > 0 && (
                  <div className="col-span-3 rounded-lg border border-red-200 overflow-hidden max-h-52 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-red-50"><tr><th className="text-left p-2">Row</th><th className="text-left p-2">Code</th><th className="text-left p-2">Error</th></tr></thead>
                      <tbody>
                        {importResult.errors.map((e, i) => (
                          <tr key={`${e.row}-${e.store_code || i}`} className="border-t border-red-100">
                            <td className="p-2">{e.row}</td><td className="p-2 font-mono">{e.store_code}</td><td className="p-2 text-red-700">{e.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImport}>{importResult ? "Close" : "Cancel"}</Button>
            {!importResult && (
              <Button data-testid="confirm-stores-import-btn" onClick={submitImport} disabled={!importRows.length || importing} className="bg-brand-red hover:bg-[#B31721]">
                <Upload className="h-4 w-4 mr-2" />
                {importing ? "Importing…" : `Import ${importRows.length} row(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wipe demo dialog */}
      <Dialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <DialogContent data-testid="wipe-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Wipe demo data</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-900">
              This will permanently delete transactional demo data. Cannot be undone.
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="wipe-stores-checkbox" type="checkbox" checked={wipeStores} onChange={(e) => setWipeStores(e.target.checked)} className="accent-red-600 mt-1" />
              <span>Also delete <b>demo stores</b> ({stores.length} stores currently seeded).</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="wipe-users-checkbox" type="checkbox" checked={wipeUsers} onChange={(e) => setWipeUsers(e.target.checked)} className="accent-red-600 mt-1" />
              <span>Also delete <b>all non-test users</b> (the 4 test accounts are always preserved).</span>
            </label>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-red-700">
                Type "WIPE" to confirm
              </label>
              <Input data-testid="wipe-confirm-input" value={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.value)} placeholder="WIPE" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWipeOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-wipe-btn" onClick={runWipe} disabled={wiping || wipeConfirm !== "WIPE"} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-4 w-4 mr-2" /> Wipe now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
